/**
 * useDeviceHeading Hook
 *
 * Listens to the browser device-orientation events and derives a compass
 * heading in degrees (0–360, clockwise from north). Prefers the absolute
 * `deviceorientationabsolute` event when available; on iOS it reads the
 * non-standard `event.webkitCompassHeading`, otherwise it derives the
 * heading from `event.alpha`.
 *
 * Does NOT request orientation permission itself — that is performed at
 * toggle time by `requestLocationConsent`. Returns `null` while the value
 * is unsupported or not yet available.
 */
import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

/** Standard `deviceorientation` event name. */
const DEVICE_ORIENTATION_EVENT = 'deviceorientation';

/** Non-standard absolute-orientation event (Chromium); preferred when present. */
const DEVICE_ORIENTATION_ABSOLUTE_EVENT = 'deviceorientationabsolute';

/** Full circle in degrees — used to normalise headings into the 0–360 range. */
const DEGREES_IN_CIRCLE = 360;

/** Shape of the non-standard iOS compass field on a DeviceOrientationEvent. */
interface IosCompassEvent {
  webkitCompassHeading: number;
}

/** Narrowing guard for a DeviceOrientationEvent from a generic `Event`. */
function isDeviceOrientationEvent(
  event: Event
): event is DeviceOrientationEvent {
  return 'alpha' in event;
}

/** Narrowing guard for the iOS-only `webkitCompassHeading` numeric field. */
function hasWebkitCompassHeading(
  candidate: unknown
): candidate is IosCompassEvent {
  return (
    typeof candidate === 'object' &&
    candidate !== null &&
    typeof (candidate as { webkitCompassHeading?: unknown })
      .webkitCompassHeading === 'number'
  );
}

/**
 * Extracts a compass heading (0–360, clockwise from north) from a
 * device-orientation event, or `null` when no usable value is present.
 *
 * - iOS exposes `webkitCompassHeading` already measured clockwise from north.
 * - Other browsers report `alpha` counter-clockwise, so it is converted.
 */
function readHeadingFromEvent(event: DeviceOrientationEvent): number | null {
  if (hasWebkitCompassHeading(event)) {
    return normalizeHeading(event.webkitCompassHeading);
  }

  if (typeof event.alpha === 'number') {
    // `alpha` increases counter-clockwise; compass heading is clockwise.
    return normalizeHeading(DEGREES_IN_CIRCLE - event.alpha);
  }

  return null;
}

/** Wraps an arbitrary degree value into the [0, 360) range. */
function normalizeHeading(degrees: number): number {
  return (
    ((degrees % DEGREES_IN_CIRCLE) + DEGREES_IN_CIRCLE) % DEGREES_IN_CIRCLE
  );
}

/**
 * Hook returning a STABLE ref to the current device heading in degrees
 * (0–360), or `null` when the device/browser cannot provide one. The value is
 * written into the ref on every `deviceorientation` tick (tens of Hz) WITHOUT
 * triggering a re-render — read `ref.current` from a long-lived callback (e.g.
 * a `watchPosition` handler) where you need the latest heading. The listener
 * is removed on unmount.
 *
 * @example
 * const headingRef = useDeviceHeading();
 * if (headingRef.current !== null) {
 *   // rotate a direction cone by `headingRef.current` degrees
 * }
 */
export function useDeviceHeading(): RefObject<number | null> {
  const headingRef = useRef<number | null>(null);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !('DeviceOrientationEvent' in window)
    ) {
      return;
    }

    const eventName =
      'ondeviceorientationabsolute' in window
        ? DEVICE_ORIENTATION_ABSOLUTE_EVENT
        : DEVICE_ORIENTATION_EVENT;

    // `deviceorientationabsolute` is non-standard and absent from the DOM
    // typings, so the handler receives the generic `Event` and narrows.
    function handleOrientation(event: Event): void {
      if (!isDeviceOrientationEvent(event)) {
        return;
      }
      const nextHeading = readHeadingFromEvent(event);
      if (nextHeading !== null) {
        headingRef.current = nextHeading;
      }
    }

    window.addEventListener(eventName, handleOrientation);

    // `eventName` is captured here, so cleanup removes the exact listener used.
    return () => {
      window.removeEventListener(eventName, handleOrientation);
    };
  }, []);

  return headingRef;
}
